import { inject, Injectable } from '@angular/core';
import { HttpBackend, HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { none, some } from '@sapphire/result';

import { ENV_CONFIG } from '@ps/core-config';

import {
  SsoCheckContextResponseDto,
  SsoExchangeCodeResponseDto,
} from './dtos/sso.dto';
import { SsoMapper } from './mappers/sso.mapper';
import { SsoCheckContextResult } from '../models/sso.model';
import { User } from '../models/user.model';

@Injectable({ providedIn: 'root' })
export class SsoApi {
  private readonly _env = inject(ENV_CONFIG);
  // Bypass auth interceptors — SSO endpoints are pre-auth and must not carry a session token.
  private readonly _http = new HttpClient(inject(HttpBackend));

  private readonly _baseUrl = `${this._env.authUrl}/oidc/interaction`;
  private readonly _mapper = new SsoMapper();

  public checkContext(email: string): Observable<SsoCheckContextResult> {
    const params = new HttpParams().set('id', email);
    return this._http
      .get<SsoCheckContextResponseDto>(`${this._baseUrl}/check-context`, {
        params,
      })
      .pipe(
        map((dto) => ({
          config:
            dto.valid && dto.config
              ? some({
                  clientId: dto.config.clientId,
                  redirectUri: dto.config.redirectUri,
                  scope: dto.config.scope,
                  responseType: dto.config.responseType,
                  redirectTimeout: dto.config.redirectTimeout,
                  upstreamId: dto.config.upstreamId,
                })
              : none,
        })),
      );
  }

  public exchangeCode(code: string): Observable<User> {
    return this._http
      .post<SsoExchangeCodeResponseDto>(`${this._baseUrl}/exchange`, { code })
      .pipe(map((response) => this._mapper.fromDto(response)));
  }
}
